// The two questions a module asks the viewer's browser: less motion (R184),
// and a hidden document.
//
// Four callers ask the reduced-motion question — the spawn arrival, the
// seaquake tremor, the chrome dock-in and the season banner — and three of
// them used to carry their own copy of the `matchMedia` call. The hidden
// question had five copies across the layers, the chrome, the socket and
// the arcade deck. One copy of each keeps the answer identical everywhere,
// and an engine-free module lets a caller that never touches a renderer
// ask without pulling an engine into its import graph
// (`fast/nopixi.test.ts`).
//
// The same three arrivals end on the first input of any kind (R163), so the
// pair of window listeners that skips them lives here too.

/**
 * True when the viewer asked the OS for less motion. A browser without
 * `matchMedia`, and a `matchMedia` that throws, both say no.
 */
export function reducedMotion(): boolean {
  return motionReader();
}

/**
 * Test only (R3059): sets the answer of `reducedMotion`, so a suite that
 * runs in Node needs no `window.matchMedia`. Null puts the media query
 * back. No page calls it, and `fast/nopixi.test.ts` fails when a module
 * outside test code names it.
 */
export function setReducedMotionReader(read: (() => boolean) | null): void {
  motionReader = read ?? mediaReducedMotion;
}

/** The media query `prefers-reduced-motion: reduce`, the default reader. */
function mediaReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** What `reducedMotion` asks: the media query, unless a test set a reader. */
let motionReader: () => boolean = mediaReducedMotion;

/** True where the document is hidden, so a frame draws nothing and a
 *  clock does not tick (§10.4). No document at all says no. */
export function hiddenDocument(): boolean {
  return typeof document !== 'undefined' && document.hidden === true;
}

/**
 * Puts `skip` on every pointerdown and every keydown of the window, in the
 * capture phase, so the first input of any kind skips an arrival (R163).
 * The event is not swallowed: the map is live under the arrival.
 */
export function skipOnInput(skip: () => void): void {
  window.addEventListener('pointerdown', skip, true);
  window.addEventListener('keydown', skip, true);
}

/** Takes `skip` off again. A listener that is not on the window comes off
 *  as a no-op. */
export function stopSkipOnInput(skip: () => void): void {
  window.removeEventListener('pointerdown', skip, true);
  window.removeEventListener('keydown', skip, true);
}
